/**
 * BoP Network Definitions for the NS Solver.
 *
 * Each BoP is built as a proper NS pipe network with:
 *   - Explicit pipes between every component (with correct D, L, roughness)
 *   - Correct initial P, T, ṁ at every state point
 *   - Proper component parameterization matching reference reports
 *   - Boundary conditions that close the cycle
 *
 * Reference: Deep Atomic MK60 Analysis Reports (Feb 2025)
 *   T_SG = 280°C, P_SG = 6.4 MPa, T_cond = 50°C
 *   η_T = 85%, η_P = 80%, η_G = 98%
 *   W_DC = 60 MW, Q_cool = 60 MW
 */

import type { NSSolverState, SIMPLEConfig } from '../types';
import { DEFAULT_SIMPLE_CONFIG } from '../types';
import type {} from '../types'; // Ensure SIMPLEConfig is available
import { createPipe } from '../network/nsPipe';
import { createJunction } from '../network/nsJunction';
import { createNSSolverState } from '../network/nsNetwork';
import { NSBoundary } from '../components/nsBoundary';
import { NSPump } from '../components/nsPump';
import { NSTurbine } from '../components/nsTurbine';
import { NSBoiler } from '../components/nsBoiler';
import { NSCondenser } from '../components/nsCondenser';
import { NSHeatExchanger } from '../components/nsHeatExchanger';
import { propsFromPT_SI, propsFromPH_SI } from '../core/fluidAdapter';

// Common parameters from reference reports
const P_SG = 6.4e6;        // Pa — steam generator exit pressure
const T_SG = 553.15;       // K — 280°C
const P_HPT_OUT = 2.24e6;  // Pa — HPT exit (65% drop from 6.4 MPa)
const H_HPT_OUT = 2400e3;  // J/kg — HPT exit enthalpy (~wet steam, Q≈0.84)
const P_REHEAT_OUT = 2.2e6;// Pa — after reheater pressure drop
const T_REHEAT = 548.15;   // K — 275°C (reheater outlet, 5°C approach)
const P_COND = 12350;      // Pa — condenser pressure (T_sat ≈ 50°C)
const T_COND = 323.15;     // K — 50°C (condenser exit, saturated liquid)
const T_OFWH = 393.15;     // K — ~120°C (OFWH exit)
const MDOT_INIT = 110;     // kg/s — approximate initial mass flow

// Pipe specs — representative of power plant piping
// Longer pipes distribute pressure gradients, improving SIMPLE stability
const MAIN_PIPE_D = 0.3;   // m — 300mm main steam line
const SMALL_PIPE_D = 0.15; // m — 150mm auxiliary lines
const ROUGHNESS = 0.00005; // m — commercial steel
const MAIN_PIPE_L = 50;    // m — main steam line length (realistic for power plant)
const SHORT_PIPE_L = 20;   // m — shorter connections
const LONG_PIPE_L = 100;   // m — long runs (condenser, BFP return)

// SIMPLE config tuned for closed-loop thermodynamic cycles
const BOP_SIMPLE_CONFIG: SIMPLEConfig = {
  ...DEFAULT_SIMPLE_CONFIG,
  maxOuterIterations: 50,
  pressureRelaxation: 0.05,   // Very gentle pressure correction for large ΔP
  velocityRelaxation: 0.1,    // Gentle velocity correction
  enthalpyRelaxation: 0.3,    // Moderate enthalpy correction
};

/**
 * Helper: create a pipe initialized from pressure and enthalpy (not temperature).
 * This avoids IAPWS-97 PT lookup failures in two-phase regions.
 */
function makePipe(
  id: string, D: number, L: number,
  P_Pa: number, h_Jkg: number, mdot: number,
  nCells: number = 3,
): ReturnType<typeof createPipe> {
  // Initialize pipe with enthalpy directly
  const pipe = createPipe(id, D, L, ROUGHNESS, 0, nCells, P_Pa, 293.15, mdot);

  // Override cell enthalpies with correct values and update properties
  for (let i = 0; i < pipe.nCells; i++) {
    pipe.cells[i].P = P_Pa;
    pipe.cells[i].h = h_Jkg;
    try {
      const props = propsFromPH_SI(P_Pa, h_Jkg);
      pipe.cells[i].rho = props.rho;
      pipe.cells[i].T = props.T;
      pipe.cells[i].c = props.c;
      pipe.cells[i].mu = props.mu;
      pipe.cells[i].cp = props.cp;
      pipe.cells[i].x = props.x;
      pipe.cells[i].phase = props.phase;
      pipe.cells[i].u_int = props.u_int;
    } catch {
      // Keep defaults
    }
    // Copy to old state
    Object.assign(pipe.cells_old[i], pipe.cells[i]);
  }

  return pipe;
}

/**
 * BoP-101: Electric Vapour Compression (EVC)
 *
 * Cycle: SG → HPT → Reheater → LPT → Condenser → CF Pump → OFWH → BF Pump → SG
 * FWH extraction from between reheater and LPT
 * EVC compressor draws electrical power (not modeled in pipe network)
 *
 * State points (approximate):
 *   1. SG exit:       T=280°C, P=6.4 MPa
 *   2. HPT exit:      T=151°C, P=2.24 MPa
 *   3. Reheat exit:   T=275°C, P=2.20 MPa
 *   4. LPT exit:      T=50°C,  P=12.35 kPa
 *   5. Condenser exit: T=50°C,  P=12.1 kPa (sat liquid)
 *   6. CF Pump exit:  T=50°C,  P=2.2 MPa
 *   7. OFWH exit:     T=~120°C, P=2.2 MPa
 *   8. BF Pump exit:  T=~120°C, P=6.52 MPa
 */
export function createBop101NS(config: SIMPLEConfig = BOP_SIMPLE_CONFIG): NSSolverState {
  const pipes = new Map();
  const junctions = new Map();
  const components = new Map();

  // --- Pipes ---
  // Each pipe initialized with correct P and h at each state point
  const h_sg = propsH(T_SG, P_SG);                     // ~2781 kJ/kg → J/kg
  const h_hpt_out = H_HPT_OUT;                          // ~2400 kJ/kg (wet steam)
  const h_reheat = propsH(T_REHEAT, P_REHEAT_OUT);     // ~3010 kJ/kg (superheated after reheat)
  const h_cond = propsH(T_COND, P_COND);               // ~209 kJ/kg (sat liquid at 50°C)
  const h_ofwh = propsH(T_OFWH, P_REHEAT_OUT);         // ~505 kJ/kg (subcooled at ~120°C)

  pipes.set('p_sg_hpt',       makePipe('p_sg_hpt',       MAIN_PIPE_D, MAIN_PIPE_L,  P_SG,          h_sg,       MDOT_INIT, 5));
  pipes.set('p_hpt_reheat',   makePipe('p_hpt_reheat',   MAIN_PIPE_D, SHORT_PIPE_L, P_HPT_OUT,     h_hpt_out,  MDOT_INIT, 5));
  pipes.set('p_reheat_lpt',   makePipe('p_reheat_lpt',   MAIN_PIPE_D, MAIN_PIPE_L,  P_REHEAT_OUT,  h_reheat,   MDOT_INIT * 0.85, 5));
  pipes.set('p_lpt_cond',     makePipe('p_lpt_cond',     MAIN_PIPE_D, LONG_PIPE_L,  P_COND,        h_cond,     MDOT_INIT * 0.85, 10));
  pipes.set('p_cond_cfp',     makePipe('p_cond_cfp',     SMALL_PIPE_D,SHORT_PIPE_L, P_COND,        h_cond,     MDOT_INIT * 0.85, 5));
  pipes.set('p_cfp_ofwh',     makePipe('p_cfp_ofwh',     SMALL_PIPE_D,MAIN_PIPE_L,  P_REHEAT_OUT,  h_cond,     MDOT_INIT * 0.85, 5));
  pipes.set('p_fwh_extract',  makePipe('p_fwh_extract',  SMALL_PIPE_D,SHORT_PIPE_L, P_REHEAT_OUT,  h_reheat,   MDOT_INIT * 0.15, 5));
  pipes.set('p_ofwh_bfp',     makePipe('p_ofwh_bfp',     SMALL_PIPE_D,SHORT_PIPE_L, P_REHEAT_OUT,  h_ofwh,     MDOT_INIT, 5));
  pipes.set('p_bfp_sg',       makePipe('p_bfp_sg',       MAIN_PIPE_D, LONG_PIPE_L,  P_SG,          h_ofwh,     MDOT_INIT, 10));

  // --- Components ---
  components.set('sg', new NSBoiler('sg', { T_out: T_SG, P_out: P_SG }));
  components.set('hpt', new NSTurbine('hpt', { P_outlet: P_HPT_OUT, efficiency: 0.85 }));
  components.set('reheater', new NSBoiler('reheater', { T_out: T_REHEAT, P_out: 2.2e6 }));
  components.set('lpt', new NSTurbine('lpt', { P_outlet: P_COND, efficiency: 0.85 }));
  components.set('condenser', new NSCondenser('condenser', { P_cond: P_COND }));
  components.set('cf_pump', new NSPump('cf_pump', { ratedHead: 225, ratedFlow: MDOT_INIT * 0.85, rho: 988 }));
  components.set('ofwh', new NSHeatExchanger('ofwh', { Q_rate: 0 })); // Adiabatic mixing
  components.set('bf_pump', new NSPump('bf_pump', { ratedHead: 440, ratedFlow: MDOT_INIT, rho: 950 }));

  // --- Junctions ---
  junctions.set('j_sg_out', createJunction('j_sg_out', 'component',
    [{ pipeId: 'p_sg_hpt', faceIndex: 0, direction: 'outflow' }], P_SG, h_sg, 'sg'));

  junctions.set('j_hpt_out', createJunction('j_hpt_out', 'component',
    [{ pipeId: 'p_sg_hpt', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_hpt_reheat', faceIndex: 0, direction: 'outflow' }], P_HPT_OUT, h_hpt_out, 'hpt'));

  junctions.set('j_reheat_out', createJunction('j_reheat_out', 'split',
    [{ pipeId: 'p_hpt_reheat', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_reheat_lpt', faceIndex: 0, direction: 'outflow' },
     { pipeId: 'p_fwh_extract', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_reheat, 'reheater'));

  junctions.set('j_lpt_out', createJunction('j_lpt_out', 'component',
    [{ pipeId: 'p_reheat_lpt', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_lpt_cond', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond, 'lpt'));

  // Condenser outlet is the pressure REFERENCE for the cycle — fixed at P_cond
  junctions.set('j_cond_out', createJunction('j_cond_out', 'boundary',
    [{ pipeId: 'p_lpt_cond', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_cond_cfp', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond));

  junctions.set('j_cfp_out', createJunction('j_cfp_out', 'component',
    [{ pipeId: 'p_cond_cfp', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_cfp_ofwh', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_cond, 'cf_pump'));

  junctions.set('j_ofwh', createJunction('j_ofwh', 'merge',
    [{ pipeId: 'p_cfp_ofwh', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_fwh_extract', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_ofwh_bfp', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_ofwh));

  junctions.set('j_bfp_out', createJunction('j_bfp_out', 'component',
    [{ pipeId: 'p_ofwh_bfp', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_bfp_sg', faceIndex: 0, direction: 'outflow' }], P_SG, h_ofwh, 'bf_pump'));

  // SG inlet is also a pressure reference at boiler pressure
  junctions.set('j_sg_in', createJunction('j_sg_in', 'boundary',
    [{ pipeId: 'p_bfp_sg', faceIndex: 3, direction: 'inflow' }], P_SG, h_ofwh));

  return createNSSolverState(pipes, junctions, components, config);
}

/**
 * BoP-102: Steam Vapour Compression (SVC)
 *
 * Same as EVC but with SVC branch: after reheater, steam splits to
 * LPT (main) and SVC turbine (drives compressor).
 * SVC turbine exhaust → SVC condenser → SVC pump → mixer → OFWH
 */
export function createBop102NS(config: SIMPLEConfig = BOP_SIMPLE_CONFIG): NSSolverState {
  const pipes = new Map();
  const junctions = new Map();
  const components = new Map();

  const mdot_main = MDOT_INIT * 0.83; // ~83% to main path
  const mdot_svc = MDOT_INIT * 0.17;  // ~17% to SVC

  // --- Pipes initialized with pressure and enthalpy ---
  const h_sg = propsH(T_SG, P_SG);
  const h_hpt_out = H_HPT_OUT;
  const h_reheat = propsH(T_REHEAT, P_REHEAT_OUT);
  const h_cond = propsH(T_COND, P_COND);
  const h_ofwh = propsH(T_OFWH, P_REHEAT_OUT);

  // Main steam path
  pipes.set('p_sg_hpt',       makePipe('p_sg_hpt',       MAIN_PIPE_D, 5,  P_SG,          h_sg,       MDOT_INIT));
  pipes.set('p_hpt_reheat',   makePipe('p_hpt_reheat',   MAIN_PIPE_D, 3,  P_HPT_OUT,     h_hpt_out,  MDOT_INIT));
  pipes.set('p_reheat_split', makePipe('p_reheat_split', MAIN_PIPE_D, 2,  P_REHEAT_OUT,  h_reheat,   MDOT_INIT));

  // Main branch: LPT path
  pipes.set('p_split_lpt',    makePipe('p_split_lpt',    MAIN_PIPE_D, 3,  P_REHEAT_OUT,  h_reheat,   mdot_main * 0.85));
  pipes.set('p_fwh_extract',  makePipe('p_fwh_extract',  SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_reheat,   mdot_main * 0.15));
  pipes.set('p_lpt_cond',     makePipe('p_lpt_cond',     MAIN_PIPE_D, 8,  P_COND,        h_cond,     mdot_main * 0.85));
  pipes.set('p_cond_cfp',     makePipe('p_cond_cfp',     SMALL_PIPE_D, 3, P_COND,        h_cond,     mdot_main * 0.85));

  // SVC branch
  pipes.set('p_split_svc',    makePipe('p_split_svc',    SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_reheat,   mdot_svc));
  pipes.set('p_svc_cond',     makePipe('p_svc_cond',     SMALL_PIPE_D, 5, P_COND,        h_cond,     mdot_svc));
  pipes.set('p_svccond_pump', makePipe('p_svccond_pump', SMALL_PIPE_D, 2, P_COND,        h_cond,     mdot_svc));

  // Merge and return
  pipes.set('p_cfp_mixer',    makePipe('p_cfp_mixer',    SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_cond,     mdot_main * 0.85));
  pipes.set('p_svcpump_mixer',makePipe('p_svcpump_mixer',SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_cond,     mdot_svc));
  pipes.set('p_mixer_ofwh',   makePipe('p_mixer_ofwh',   SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_cond,     MDOT_INIT * 0.85));
  pipes.set('p_ofwh_bfp',     makePipe('p_ofwh_bfp',     SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_ofwh,     MDOT_INIT));
  pipes.set('p_bfp_sg',       makePipe('p_bfp_sg',       MAIN_PIPE_D, 10, P_SG,          h_ofwh,     MDOT_INIT));

  // --- Components ---
  components.set('sg', new NSBoiler('sg', { T_out: T_SG, P_out: P_SG }));
  components.set('hpt', new NSTurbine('hpt', { P_outlet: P_HPT_OUT, efficiency: 0.85 }));
  components.set('reheater', new NSBoiler('reheater', { T_out: T_REHEAT, P_out: 2.2e6 }));
  components.set('lpt', new NSTurbine('lpt', { P_outlet: P_COND, efficiency: 0.85 }));
  components.set('condenser', new NSCondenser('condenser', { P_cond: P_COND }));
  components.set('cf_pump', new NSPump('cf_pump', { ratedHead: 225, ratedFlow: mdot_main * 0.85, rho: 988 }));
  components.set('svc_turbine', new NSTurbine('svc_turbine', { P_outlet: P_COND, efficiency: 0.85 }));
  components.set('svc_condenser', new NSCondenser('svc_condenser', { P_cond: P_COND }));
  components.set('svc_pump', new NSPump('svc_pump', { ratedHead: 225, ratedFlow: mdot_svc, rho: 988 }));
  components.set('bf_pump', new NSPump('bf_pump', { ratedHead: 440, ratedFlow: MDOT_INIT, rho: 950 }));

  // --- Junctions ---
  junctions.set('j_sg_out', createJunction('j_sg_out', 'component',
    [{ pipeId: 'p_sg_hpt', faceIndex: 0, direction: 'outflow' }], P_SG, h_sg, 'sg'));

  junctions.set('j_hpt_out', createJunction('j_hpt_out', 'component',
    [{ pipeId: 'p_sg_hpt', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_hpt_reheat', faceIndex: 0, direction: 'outflow' }], P_HPT_OUT, h_hpt_out, 'hpt'));

  junctions.set('j_reheat_out', createJunction('j_reheat_out', 'component',
    [{ pipeId: 'p_hpt_reheat', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_reheat_split', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_reheat, 'reheater'));

  junctions.set('j_split', createJunction('j_split', 'split',
    [{ pipeId: 'p_reheat_split', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_split_lpt', faceIndex: 0, direction: 'outflow' },
     { pipeId: 'p_fwh_extract', faceIndex: 0, direction: 'outflow' },
     { pipeId: 'p_split_svc', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_reheat));

  junctions.set('j_lpt_out', createJunction('j_lpt_out', 'component',
    [{ pipeId: 'p_split_lpt', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_lpt_cond', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond, 'lpt'));

  junctions.set('j_cond_out', createJunction('j_cond_out', 'boundary',
    [{ pipeId: 'p_lpt_cond', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_cond_cfp', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond));

  junctions.set('j_cfp_out', createJunction('j_cfp_out', 'component',
    [{ pipeId: 'p_cond_cfp', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_cfp_mixer', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_cond, 'cf_pump'));

  junctions.set('j_svc_turb_out', createJunction('j_svc_turb_out', 'component',
    [{ pipeId: 'p_split_svc', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_svc_cond', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond, 'svc_turbine'));

  junctions.set('j_svc_cond_out', createJunction('j_svc_cond_out', 'component',
    [{ pipeId: 'p_svc_cond', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_svccond_pump', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond, 'svc_condenser'));

  junctions.set('j_svc_pump_out', createJunction('j_svc_pump_out', 'component',
    [{ pipeId: 'p_svccond_pump', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_svcpump_mixer', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_cond, 'svc_pump'));

  junctions.set('j_mixer', createJunction('j_mixer', 'merge',
    [{ pipeId: 'p_cfp_mixer', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_svcpump_mixer', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_mixer_ofwh', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_cond));

  junctions.set('j_ofwh', createJunction('j_ofwh', 'merge',
    [{ pipeId: 'p_mixer_ofwh', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_fwh_extract', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_ofwh_bfp', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_ofwh));

  junctions.set('j_bfp_out', createJunction('j_bfp_out', 'component',
    [{ pipeId: 'p_ofwh_bfp', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_bfp_sg', faceIndex: 0, direction: 'outflow' }], P_SG, h_ofwh, 'bf_pump'));

  junctions.set('j_sg_in', createJunction('j_sg_in', 'boundary',
    [{ pipeId: 'p_bfp_sg', faceIndex: 3, direction: 'inflow' }], P_SG, h_ofwh));

  return createNSSolverState(pipes, junctions, components, config);
}

/**
 * BoP-103: Steam Absorption Chiller (SAC)
 *
 * Cycle: SG → HPT → Reheater → LPT → SAC (absorbs LP heat) → Condenser → CF Pump → OFWH → BF Pump → SG
 * FWH extraction from between reheater and LPT
 * SAC receives LP exhaust — acts as primary heat sink for cooling
 * ACC (condenser) ensures full condensation after SAC
 */
export function createBop103NS(config: SIMPLEConfig = BOP_SIMPLE_CONFIG): NSSolverState {
  const pipes = new Map();
  const junctions = new Map();
  const components = new Map();

  const h_sg = propsH(T_SG, P_SG);
  const h_hpt_out = H_HPT_OUT;
  const h_reheat = propsH(T_REHEAT, P_REHEAT_OUT);
  const h_cond = propsH(T_COND, P_COND);
  const h_ofwh = propsH(T_OFWH, P_REHEAT_OUT);

  // Pipes — same main path as EVC but with SAC between LPT and condenser
  pipes.set('p_sg_hpt',       makePipe('p_sg_hpt',       MAIN_PIPE_D, 5,  P_SG,          h_sg,       MDOT_INIT));
  pipes.set('p_hpt_reheat',   makePipe('p_hpt_reheat',   MAIN_PIPE_D, 3,  P_HPT_OUT,     h_hpt_out,  MDOT_INIT));
  pipes.set('p_reheat_lpt',   makePipe('p_reheat_lpt',   MAIN_PIPE_D, 5,  P_REHEAT_OUT,  h_reheat,   MDOT_INIT * 0.85));
  pipes.set('p_fwh_extract',  makePipe('p_fwh_extract',  SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_reheat,   MDOT_INIT * 0.15));
  pipes.set('p_lpt_sac',      makePipe('p_lpt_sac',      MAIN_PIPE_D, 5,  P_COND,        h_cond,     MDOT_INIT * 0.85));
  pipes.set('p_sac_cond',     makePipe('p_sac_cond',     MAIN_PIPE_D, 3,  P_COND,        h_cond,     MDOT_INIT * 0.85));
  pipes.set('p_cond_cfp',     makePipe('p_cond_cfp',     SMALL_PIPE_D, 3, P_COND,        h_cond,     MDOT_INIT * 0.85));
  pipes.set('p_cfp_ofwh',     makePipe('p_cfp_ofwh',     SMALL_PIPE_D, 5, P_REHEAT_OUT,  h_cond,     MDOT_INIT * 0.85));
  pipes.set('p_ofwh_bfp',     makePipe('p_ofwh_bfp',     SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_ofwh,     MDOT_INIT));
  pipes.set('p_bfp_sg',       makePipe('p_bfp_sg',       MAIN_PIPE_D, 10, P_SG,          h_ofwh,     MDOT_INIT));

  // Components
  components.set('sg', new NSBoiler('sg', { T_out: T_SG, P_out: P_SG }));
  components.set('hpt', new NSTurbine('hpt', { P_outlet: P_HPT_OUT, efficiency: 0.85 }));
  components.set('reheater', new NSBoiler('reheater', { T_out: T_REHEAT, P_out: P_REHEAT_OUT }));
  components.set('lpt', new NSTurbine('lpt', { P_outlet: P_COND, efficiency: 0.85 }));
  components.set('sac', new NSHeatExchanger('sac', { Q_rate: -60e6 })); // SAC removes 60 MW
  components.set('condenser', new NSCondenser('condenser', { P_cond: P_COND }));
  components.set('cf_pump', new NSPump('cf_pump', { ratedHead: 225, ratedFlow: MDOT_INIT * 0.85, rho: 988 }));
  components.set('bf_pump', new NSPump('bf_pump', { ratedHead: 440, ratedFlow: MDOT_INIT, rho: 950 }));

  // Junctions
  junctions.set('j_sg_out', createJunction('j_sg_out', 'component',
    [{ pipeId: 'p_sg_hpt', faceIndex: 0, direction: 'outflow' }], P_SG, h_sg, 'sg'));
  junctions.set('j_hpt_out', createJunction('j_hpt_out', 'component',
    [{ pipeId: 'p_sg_hpt', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_hpt_reheat', faceIndex: 0, direction: 'outflow' }], P_HPT_OUT, h_hpt_out, 'hpt'));
  junctions.set('j_reheat_out', createJunction('j_reheat_out', 'split',
    [{ pipeId: 'p_hpt_reheat', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_reheat_lpt', faceIndex: 0, direction: 'outflow' },
     { pipeId: 'p_fwh_extract', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_reheat, 'reheater'));
  junctions.set('j_lpt_out', createJunction('j_lpt_out', 'component',
    [{ pipeId: 'p_reheat_lpt', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_lpt_sac', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond, 'lpt'));
  junctions.set('j_sac_out', createJunction('j_sac_out', 'component',
    [{ pipeId: 'p_lpt_sac', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_sac_cond', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond, 'sac'));
  junctions.set('j_cond_out', createJunction('j_cond_out', 'boundary',
    [{ pipeId: 'p_sac_cond', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_cond_cfp', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond));
  junctions.set('j_cfp_out', createJunction('j_cfp_out', 'component',
    [{ pipeId: 'p_cond_cfp', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_cfp_ofwh', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_cond, 'cf_pump'));
  junctions.set('j_ofwh', createJunction('j_ofwh', 'merge',
    [{ pipeId: 'p_cfp_ofwh', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_fwh_extract', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_ofwh_bfp', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_ofwh));
  junctions.set('j_bfp_out', createJunction('j_bfp_out', 'component',
    [{ pipeId: 'p_ofwh_bfp', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_bfp_sg', faceIndex: 0, direction: 'outflow' }], P_SG, h_ofwh, 'bf_pump'));
  junctions.set('j_sg_in', createJunction('j_sg_in', 'boundary',
    [{ pipeId: 'p_bfp_sg', faceIndex: 3, direction: 'inflow' }], P_SG, h_ofwh));

  return createNSSolverState(pipes, junctions, components, config);
}

/**
 * BoP-104: Steam Jet Cooler (SJC)
 *
 * Same structure as SVC (BoP-102) but the SVC turbine branch is replaced by:
 * Splitter → SJC (steam jet cooler condenses steam at 40°C) → SJC Pump → Mixer
 */
export function createBop104NS(config: SIMPLEConfig = BOP_SIMPLE_CONFIG): NSSolverState {
  const pipes = new Map();
  const junctions = new Map();
  const components = new Map();

  const mdot_main = MDOT_INIT * 0.80;
  const mdot_sjc = MDOT_INIT * 0.20;
  const h_sg = propsH(T_SG, P_SG);
  const h_hpt_out = H_HPT_OUT;
  const h_reheat = propsH(T_REHEAT, P_REHEAT_OUT);
  const h_cond = propsH(T_COND, P_COND);
  const h_sjc_cond = propsH(313.15, P_COND); // 40°C SJC condenser
  const h_ofwh = propsH(T_OFWH, P_REHEAT_OUT);

  // Main steam path
  pipes.set('p_sg_hpt',       makePipe('p_sg_hpt',       MAIN_PIPE_D, 5,  P_SG,          h_sg,       MDOT_INIT));
  pipes.set('p_hpt_reheat',   makePipe('p_hpt_reheat',   MAIN_PIPE_D, 3,  P_HPT_OUT,     h_hpt_out,  MDOT_INIT));
  pipes.set('p_reheat_split', makePipe('p_reheat_split', MAIN_PIPE_D, 2,  P_REHEAT_OUT,  h_reheat,   MDOT_INIT));
  // Main branch
  pipes.set('p_split_lpt',    makePipe('p_split_lpt',    MAIN_PIPE_D, 3,  P_REHEAT_OUT,  h_reheat,   mdot_main * 0.85));
  pipes.set('p_fwh_extract',  makePipe('p_fwh_extract',  SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_reheat,   mdot_main * 0.15));
  pipes.set('p_lpt_cond',     makePipe('p_lpt_cond',     MAIN_PIPE_D, 8,  P_COND,        h_cond,     mdot_main * 0.85));
  pipes.set('p_cond_cfp',     makePipe('p_cond_cfp',     SMALL_PIPE_D, 3, P_COND,        h_cond,     mdot_main * 0.85));
  // SJC branch — steam condenses at 40°C
  pipes.set('p_split_sjc',    makePipe('p_split_sjc',    SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_reheat,   mdot_sjc));
  pipes.set('p_sjc_pump',     makePipe('p_sjc_pump',     SMALL_PIPE_D, 3, P_COND,        h_sjc_cond, mdot_sjc));
  // Merge and return
  pipes.set('p_cfp_mixer',    makePipe('p_cfp_mixer',    SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_cond,     mdot_main * 0.85));
  pipes.set('p_sjcpump_mixer',makePipe('p_sjcpump_mixer',SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_sjc_cond, mdot_sjc));
  pipes.set('p_mixer_ofwh',   makePipe('p_mixer_ofwh',   SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_cond,     MDOT_INIT * 0.85));
  pipes.set('p_ofwh_bfp',     makePipe('p_ofwh_bfp',     SMALL_PIPE_D, 3, P_REHEAT_OUT,  h_ofwh,     MDOT_INIT));
  pipes.set('p_bfp_sg',       makePipe('p_bfp_sg',       MAIN_PIPE_D, 10, P_SG,          h_ofwh,     MDOT_INIT));

  // Components
  components.set('sg', new NSBoiler('sg', { T_out: T_SG, P_out: P_SG }));
  components.set('hpt', new NSTurbine('hpt', { P_outlet: P_HPT_OUT, efficiency: 0.85 }));
  components.set('reheater', new NSBoiler('reheater', { T_out: T_REHEAT, P_out: P_REHEAT_OUT }));
  components.set('lpt', new NSTurbine('lpt', { P_outlet: P_COND, efficiency: 0.85 }));
  components.set('condenser', new NSCondenser('condenser', { P_cond: P_COND }));
  components.set('cf_pump', new NSPump('cf_pump', { ratedHead: 225, ratedFlow: mdot_main * 0.85, rho: 988 }));
  components.set('sjc', new NSCondenser('sjc', { P_cond: 7384 })); // P_sat(40°C) ≈ 7.384 kPa
  components.set('sjc_pump', new NSPump('sjc_pump', { ratedHead: 225, ratedFlow: mdot_sjc, rho: 992 }));
  components.set('bf_pump', new NSPump('bf_pump', { ratedHead: 440, ratedFlow: MDOT_INIT, rho: 950 }));

  // Junctions
  junctions.set('j_sg_out', createJunction('j_sg_out', 'component',
    [{ pipeId: 'p_sg_hpt', faceIndex: 0, direction: 'outflow' }], P_SG, h_sg, 'sg'));
  junctions.set('j_hpt_out', createJunction('j_hpt_out', 'component',
    [{ pipeId: 'p_sg_hpt', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_hpt_reheat', faceIndex: 0, direction: 'outflow' }], P_HPT_OUT, h_hpt_out, 'hpt'));
  junctions.set('j_reheat_out', createJunction('j_reheat_out', 'component',
    [{ pipeId: 'p_hpt_reheat', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_reheat_split', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_reheat, 'reheater'));
  junctions.set('j_split', createJunction('j_split', 'split',
    [{ pipeId: 'p_reheat_split', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_split_lpt', faceIndex: 0, direction: 'outflow' },
     { pipeId: 'p_fwh_extract', faceIndex: 0, direction: 'outflow' },
     { pipeId: 'p_split_sjc', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_reheat));
  junctions.set('j_lpt_out', createJunction('j_lpt_out', 'component',
    [{ pipeId: 'p_split_lpt', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_lpt_cond', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond, 'lpt'));
  junctions.set('j_cond_out', createJunction('j_cond_out', 'boundary',
    [{ pipeId: 'p_lpt_cond', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_cond_cfp', faceIndex: 0, direction: 'outflow' }], P_COND, h_cond));
  junctions.set('j_cfp_out', createJunction('j_cfp_out', 'component',
    [{ pipeId: 'p_cond_cfp', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_cfp_mixer', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_cond, 'cf_pump'));
  junctions.set('j_sjc_out', createJunction('j_sjc_out', 'component',
    [{ pipeId: 'p_split_sjc', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_sjc_pump', faceIndex: 0, direction: 'outflow' }], P_COND, h_sjc_cond, 'sjc'));
  junctions.set('j_sjc_pump_out', createJunction('j_sjc_pump_out', 'component',
    [{ pipeId: 'p_sjc_pump', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_sjcpump_mixer', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_sjc_cond, 'sjc_pump'));
  junctions.set('j_mixer', createJunction('j_mixer', 'merge',
    [{ pipeId: 'p_cfp_mixer', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_sjcpump_mixer', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_mixer_ofwh', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_cond));
  junctions.set('j_ofwh', createJunction('j_ofwh', 'merge',
    [{ pipeId: 'p_mixer_ofwh', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_fwh_extract', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_ofwh_bfp', faceIndex: 0, direction: 'outflow' }], P_REHEAT_OUT, h_ofwh));
  junctions.set('j_bfp_out', createJunction('j_bfp_out', 'component',
    [{ pipeId: 'p_ofwh_bfp', faceIndex: 3, direction: 'inflow' },
     { pipeId: 'p_bfp_sg', faceIndex: 0, direction: 'outflow' }], P_SG, h_ofwh, 'bf_pump'));
  junctions.set('j_sg_in', createJunction('j_sg_in', 'boundary',
    [{ pipeId: 'p_bfp_sg', faceIndex: 3, direction: 'inflow' }], P_SG, h_ofwh));

  return createNSSolverState(pipes, junctions, components, config);
}

/**
 * Helper: get enthalpy in J/kg from T(K) and P(Pa) via IAPWS-97.
 */
function propsH(T_K: number, P_Pa: number): number {
  try {
    return propsFromPT_SI(
      Math.max(611.657, Math.min(100e6, P_Pa)),
      Math.max(273.16, Math.min(623.15, T_K))
    ).h;
  } catch {
    // Fallback for states near saturation dome
    if (T_K > 373.15) return (2500 + 2.0 * (T_K - 373.15)) * 1000;
    return 4182 * (T_K - 273.15);
  }
}
